import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { sheetStyles } from './styles';

const Sheet = ({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Root>): React.ReactElement => (
  <SheetPrimitive.Root data-slot="sheet" {...props} />
);

const SheetTrigger = ({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>): React.ReactElement => (
  <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
);

const SheetClose = ({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>): React.ReactElement => (
  <SheetPrimitive.Close data-slot="sheet-close" {...props} />
);

const SheetPortal = ({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>): React.ReactElement => (
  <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
);

const SheetOverlay = ({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>): React.ReactElement => (
  <SheetPrimitive.Overlay
    data-slot="sheet-overlay"
    className={cn(sheetStyles.overlay, className)}
    {...props}
  />
);

const SheetContent = ({
  className,
  children,
  side = 'right',
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: 'top' | 'right' | 'bottom' | 'left';
}): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.COMMON);
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        className={cn(
          sheetStyles.content.base,
          sheetStyles.content.sides[side],
          'notranslate',
          className
        )}
        translate="no"
        {...props}
      >
        {children}
        <SheetPrimitive.Close className={sheetStyles.closeButton}>
          <XIcon className="size-4" />
          <span className="sr-only">{t('Common.close')}</span>
        </SheetPrimitive.Close>
      </SheetPrimitive.Content>
    </SheetPortal>
  );
};

const SheetHeader = ({ className, ...props }: React.ComponentProps<'div'>): React.ReactElement => (
  <div data-slot="sheet-header" className={cn(sheetStyles.header, className)} {...props} />
);

const SheetFooter = ({ className, ...props }: React.ComponentProps<'div'>): React.ReactElement => (
  <div data-slot="sheet-footer" className={cn(sheetStyles.footer, className)} {...props} />
);

const SheetTitle = ({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>): React.ReactElement => (
  <SheetPrimitive.Title
    data-slot="sheet-title"
    className={cn(sheetStyles.title, className)}
    {...props}
  />
);

const SheetDescription = ({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>): React.ReactElement => (
  <SheetPrimitive.Description
    data-slot="sheet-description"
    className={cn(sheetStyles.description, className)}
    {...props}
  />
);

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
